import { Suspense, useState } from "react"
import { enqueueSnackbar } from "notistack"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { SERVICE_CENTER_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectServiceCenterId } from "../../../../../login/loginSelectors"
import { manageBookingsForUser } from "../manageBookingsApi"
import {
    ManageBookingsFormValues,
    ManageBookingsAction,
    ManageBookingsOrderOperation,
    ManageBookingsDryRunResponse,
    ManageBookingsExcludedOrder,
} from "../manageBookingsTypes"
import { ManageBookingsForm } from "../components/ManageBookingsForm"
import { ManageBookingsConfirmDialog } from "../components/ManageBookingsConfirmDialog"
import { ManageBookingsSuccessDialog } from "../components/ManageBookingsSuccessDialog"

type PendingConfirmState = Readonly<{
    values: ManageBookingsFormValues
    orderOperation: ManageBookingsOrderOperation
    affectedOrders: number
    excludedOrders: ManageBookingsExcludedOrder[]
}>

function ManageBookings() {
    const { l10n } = useAppLocalization()
    const serviceCenterId = useAppSelector(selectServiceCenterId)
    const [pendingConfirm, setPendingConfirm] = useState<PendingConfirmState | null>(null)
    const [isConfirming, setIsConfirming] = useState(false)
    const [successAction, setSuccessAction] = useState<ManageBookingsAction | null>(null)

    const { mutateAsync } = useApiMutation(manageBookingsForUser, {})

    function buildOrderOperation(values: ManageBookingsFormValues): ManageBookingsOrderOperation | null {
        if (!values.user || !values.action) return null

        const userId = values.user.id

        if (values.action === ManageBookingsAction.CHANGE_ORGANIZER && values.newBookingOwner) {
            return {
                type: ManageBookingsAction.CHANGE_ORGANIZER,
                oldOrganizer: { type: "RESPONSIBLE", id: userId },
                newOrganizer: { type: "RESPONSIBLE", id: values.newBookingOwner.id },
                updateResourceBooking: values.updateResourceBooking,
                updateMeetingService: values.updateMeetingService,
            }
        }

        if (values.action === ManageBookingsAction.CANCEL_MEETING) {
            return {
                type: ManageBookingsAction.CANCEL_MEETING,
                oldOrganizer: { type: "CREATOR", id: userId },
                updateResourceBooking: values.updateResourceBooking,
                updateMeetingService: values.updateMeetingService,
            }
        }

        return null
    }

    const handleSubmit = async (values: ManageBookingsFormValues): Promise<void> => {
        if (!values.company || !values.user || !values.action || !serviceCenterId) {
            enqueueSnackbar({ variant: "error", message: l10n.getString("service-center-admin-organization-manage-bookings-submit-missing-data-error") })
            return
        }

        const orderOperation = buildOrderOperation(values)
        if (!orderOperation) return

        const dryRunResult = (await mutateAsync({
            serviceCenterId,
            orderOperation: { ...orderOperation, dryRun: true },
        })) as ManageBookingsDryRunResponse | undefined

        setPendingConfirm({
            values,
            orderOperation,
            affectedOrders: dryRunResult?.dryRun?.affectedOrders ?? 0,
            excludedOrders: dryRunResult?.dryRun?.excludedOrders ?? [],
        })
    }

    const handleConfirm = async () => {
        if (!pendingConfirm || !serviceCenterId) return

        setIsConfirming(true)
        try {
            await mutateAsync({ serviceCenterId, orderOperation: pendingConfirm.orderOperation })
            setSuccessAction(pendingConfirm.orderOperation.type)
            setPendingConfirm(null)
        } finally {
            setIsConfirming(false)
        }
    }

    const handleCloseDialog = () => {
        if (!isConfirming) {
            setPendingConfirm(null)
        }
    }

    return (
        <>
            <ManageBookingsForm onSubmit={handleSubmit} />
            {pendingConfirm && (
                <ManageBookingsConfirmDialog
                    open
                    onClose={handleCloseDialog}
                    onConfirm={handleConfirm}
                    isLoading={isConfirming}
                    action={pendingConfirm.orderOperation.type}
                    affectedOrders={pendingConfirm.affectedOrders}
                    excludedOrders={pendingConfirm.excludedOrders}
                    userName={pendingConfirm.values.user?.displayName ?? ""}
                    newOwnerName={pendingConfirm.values.newBookingOwner?.displayName}
                />
            )}
            {successAction && (
                <ManageBookingsSuccessDialog
                    open
                    onClose={() => setSuccessAction(null)}
                    action={successAction}
                />
            )}
        </>
    )
}

export function ServiceCenterAdminManageBookings() {
    return (
        <>
            <LocalizedStrict
                id="service-center-admin-organization-manage-bookings-page-header"
                attrs={{ title: true }}
            >
                <PageHeader
                    page={Page.SERVICE_CENTER_ADMIN_ORGANIZATION_MANAGE_BOOKINGS_ROUTE}
                    title="Manage bookings for user"
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="service-center-nav-item-home">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-organization">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-organization-manage-bookings-breadcrumb">
                            <Breadcrumb>Manage bookings for user</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <ManageBookings />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
